import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getNewSpotQuoteNoInProgress } from '@redux/common/CommonActions';
import { Col, Form, Row } from 'antd';
import moment from 'moment';
import { formateWithMoment } from '@utilz/dates';
import { routeFileSelector } from '@utilz/i18n';

import { USER_PREFERENCES } from '@/contants/common';
import { FormSchema, useRegisterForm } from '@/modules/validators';
import { CurrencySelect } from '@/views/screens/common';

import useUserPreference from '../../../../../../../redux/hooks/useUserPreference';
import FormInput from '../../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import HalWarningModal from '../../../../../../components/Modals/HalWarningModal';
import BidDurationModal from '../../../../../Shipment/components/QuoteCard/components/QuoteDetails/BidDurationModal';
import { QUOTE_STATUSES } from '../../../../../Shipment/components/QuoteCard/constants';
import useBidDuration, { initialBidDurationTimeError } from '../../../../hooks/useBidDuration';
import { BidDurationCard } from '../../../common/BidDurationCard';

const HallQuoteDetailsForm = () => {
  const [showBidDuration, setShowBidDuration] = useState(false);
  const { selectedCompany } = useSelector((state) => state.auth);
  const { quoteNo, quoteTitle, quoteKey } = useSelector((state) => state.common.currentSpotQuoteNoAndTitle.data);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user.data, shallowEqual);
  const location = useLocation();
  const prepopulatedData = location.state?.quote;

  const [form] = Form.useForm();
  useRegisterForm(form, 'quoteDetailsForm');
  const createdOn = Form.useWatch('createdOn', form);
  const bidExpirationDateValue = form.getFieldValue('bidExpirationDate');

  const { data } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);
  const createdByTitle = `${userData.firstName} ${userData.lastName}`;

  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        { name: 'currencyCode', value: data?.currencyCode },
        { name: 'bidExpirationDate', value: null },
      ]);
    }
  }, [copyQuoteKey, data?.currencyCode, form]);

  useEffect(() => {
    if (prepopulatedData) {
      form.setFieldsValue({
        quoteKey: prepopulatedData.quoteKey,
        quoteNo: prepopulatedData.quoteNumber,
        quoteTitle: prepopulatedData.quoteTitle,
      });
    }
  }, [form, prepopulatedData]);

  useEffect(() => {
    if (selectedCompany?.companyId) {
      form.setFieldValue('customerKey', selectedCompany?.companyId);
    }
  }, [selectedCompany, form.setFieldValue]);

  useEffect(() => {
    if (!prepopulatedData) {
      dispatch(getNewSpotQuoteNoInProgress());
    }
  }, [dispatch, prepopulatedData]);

  useEffect(() => {
    if (!prepopulatedData) {
      form.setFieldsValue({
        ...(quoteNo && { quoteNo }),
        ...(quoteTitle && { quoteTitle }),
        ...(quoteKey && { quoteKey }),
      });
    }
  }, [quoteNo, quoteTitle, quoteKey, prepopulatedData, form]);

  useEffect(() => {
    userData && form.setFieldValue('createdBy', createdByTitle);
  }, [form.setFieldValue, userData, createdByTitle]);

  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT);

  const { calculatedBidExpirationDate, handleOkBidDurationModal, bidDurationTimeError, setBidDurationTimeError } =
    useBidDuration({ form, setShowBidDuration });

  return (
    <>
      <Form
        form={form}
        name="quoteDetailsForm"
        initialValues={{
          createdBy: createdByTitle,
          createdOn: moment(new Date()),
          customerKey: 0,
          quoteNo: '',
          shipmentKey: 0,
          shipmentNumber: '',
          shipmentStatus: null,
          status: QUOTE_STATUSES.new,
        }}
      >
        <Row gutter={30}>
          <Col sm={6}>
            <Row gutter={15}>
              <Col sm={10}>
                <FormSchema.Field name="quoteNo">
                  {({ name, antdRule, isRequired, label }) => (
                    <FormInput
                      label={label}
                      editMode={false}
                      margin="m-0"
                      name={name}
                      data-testid="quote-no-input"
                      isRequired={isRequired}
                      rules={[antdRule]}
                    />
                  )}
                </FormSchema.Field>
              </Col>
              <Col sm={14}>
                <FormSchema.Field name="quoteTitle">
                  {({ name, label, antdRule }) => (
                    <FormInput
                      name={name}
                      label={label}
                      editMode
                      margin="m-0"
                      data-testid="quote-title-input"
                      rules={[antdRule]}
                    />
                  )}
                </FormSchema.Field>
              </Col>
            </Row>
          </Col>
          <Col sm={12}>
            <Row gutter={15}>
              <Col sm={6}>
                <FormSchema.Field name="status">
                  {({ name, antdRule, isRequired, label }) => (
                    <FormInput
                      name={name}
                      label={label}
                      editMode={false}
                      margin="m-0"
                      data-testid="quote-status-input"
                      isRequired={isRequired}
                      rules={[antdRule]}
                    />
                  )}
                </FormSchema.Field>
              </Col>
              <Col sm={6}>
                <FormSchema.Field name="currencyCode">
                  {({ name, antdRule, isRequired, label }) => (
                    <CurrencySelect
                      editMode
                      label={label}
                      name={name}
                      margin="mb-15"
                      isRequired={isRequired}
                      rules={[antdRule]}
                    />
                  )}
                </FormSchema.Field>
              </Col>
              <Col
                sm={12}
                onClick={() => setShowBidDuration(true)}
                className="d-flex flex-direction-column"
                style={{
                  cursor: bidExpirationDateValue ? 'pointer' : 'default',
                }}
                data-testid="quote-details-bid-expiration"
              >
                <FormSchema.Field name="trackingDateTime">
                  {({ name, isRequired, antdRule }) => (
                    <Form.Item name={name} isRequired={isRequired} rules={[antdRule]}>
                      <BidDurationCard dateValue={calculatedBidExpirationDate || bidExpirationDateValue} />
                    </Form.Item>
                  )}
                </FormSchema.Field>
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row gutter={15}>
              <Col sm={10}>
                <FormInput
                  name="createdBy"
                  label={t('translation:getQuote.quoteDetails.createdBy.label')}
                  editMode={false}
                  margin="m-0"
                  data-testid="quote-created-by"
                />
              </Col>
              <Col sm={14} data-testid="quote-created-on">
                <FormLabel>{t('translation:default.fields.createdOn.title')}</FormLabel>
                <div>
                  <SecondaryLabel className="f-14 fw-600 text-dark" dataTestID="user-date-format">{`${formateWithMoment(
                    createdOn,
                    userDateFormat,
                  )} | ${formateWithMoment(createdOn, 'hh:mm:ss A')}`}</SecondaryLabel>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        <HiddenFormInput name="createdOn" />
        <HiddenFormInput name="customerKey" />
        <HiddenFormInput name="bidExpirationDate" />
        <HiddenFormInput name="bidExpirationOffsetHours" />
        <HiddenFormInput name="bidExpirationTimeZoneInfo" />
        <HiddenFormInput name="bidExpirationDateCorrectionValue" />
        <HiddenFormInput name="quoteKey" />
      </Form>
      <HalWarningModal
        open={bidDurationTimeError.showModal}
        content={bidDurationTimeError.title}
        onClose={() => setBidDurationTimeError(initialBidDurationTimeError)}
        onOk={() => setBidDurationTimeError(initialBidDurationTimeError)}
        okText="Ok"
      />
      <BidDurationModal
        alertTitle="Bid Duration"
        open={showBidDuration}
        onClose={() => setShowBidDuration(false)}
        onOk={handleOkBidDurationModal}
        okText="Submit"
        setUserTZInitially
      />
    </>
  );
};

export default memo(HallQuoteDetailsForm);
